import { useCallback, useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import type { DebouncedFunc } from 'lodash';
import {
  ApiMethod,
  Control,
  FieldHandlerCallbacks,
  FormItem,
  FormItemHandlerTrigger,
  HandlerAction,
  JSONObject,
} from '../types/types';
import { HandlerRequestContext } from '../api/types/types';
import { ResponseActionHandler } from '../types/context';
import { executeHandler } from '../utils/handlers/executeHandler';
import { executeOutputHandler } from '../utils/handlers/executeOutputHandler';
import { normalizeHandlerFieldUpdates } from '../utils/handlers/normalizeHandlerFieldUpdates';
import { resolveTemplate } from '../utils/json/resolveTemplate';
import {
  HANDLER_CHANGE_DEBOUNCE_MS,
  HANDLER_TEXT_LIKE_CONTROL_TYPES,
} from '../constants';
import { debugLogger } from '../debug/DebugLogger';

const log = debugLogger.scope('handlers');

export type RunHandlerMethod = (
  method: string,
  triggerData?: JSONObject,
  formPatch?: Record<string, unknown>,
  focusedItem?: string
) => Promise<void>;

export type FireHandlerEvent = (
  fieldName: string,
  control: Control | undefined,
  action: HandlerAction,
  formPatch?: Record<string, unknown>
) => void;

// Unified fire signature shared by method and output triggers — what the
// per-trigger debounce instances wrap.
type TriggerRunner = (
  formPatch?: Record<string, unknown>,
  focusedItem?: string
) => Promise<void>;

export type FieldHandlerEngineOptions = {
  // Looks up a named handler apiMethod; undefined = not defined (warn + no-op).
  getApiMethod: (method: string) => ApiMethod | undefined;
  // Items of the form the handled fields belong to (trigger + type lookup).
  getItems: () => FormItem[];
  // Scope for the {{ }} templates of the apiMethod. formPatch carries the
  // just-changed value so the handler sees it before React commits the
  // setState. undefined = scope not available yet (warn + no-op).
  buildContext: (
    formPatch?: Record<string, unknown>,
    focusedItem?: string
  ) => HandlerRequestContext | undefined;
  // Merges the normalized handler response into the host state. Receives the
  // fire-time context back — hosts patching by position (layout objects) read
  // their capture from it instead of the current selection (which may have
  // moved while the request was in flight).
  applyFieldUpdates: (
    updates: Record<string, unknown>,
    context: HandlerRequestContext
  ) => void;
  getResponseActionHandler?: (
    name: string
  ) => ResponseActionHandler | undefined;
  handleApiError: (
    error: unknown,
    disableToasts?: boolean,
    forceModalAction?: (forceHeader: string) => Promise<void>
  ) => Promise<unknown>;
};

export type FieldHandlerEngine = {
  runMethod: RunHandlerMethod;
  fireEvent: FireHandlerEvent;
  getFieldCallbacks: (
    name: string,
    control: Control | undefined,
    triggers: FormItemHandlerTrigger[] | undefined
  ) => FieldHandlerCallbacks;
  // Header buttons must always fire — clears the value-aware dedup entry so a
  // repeated click can't be suppressed.
  clearMethodDedup: (method: string) => void;
};

// Shared handlerTriggers engine (dataset forms + layout editor): on a field
// event it calls the named apiMethod and merges the response back into the
// host state. The host plugs in its data access via options.
export const useFieldHandlerEngine = (
  options: FieldHandlerEngineOptions
): FieldHandlerEngine => {
  // Options close over the host's latest render values and change identity on
  // every render. The ref keeps runMethod/fireEvent/getFieldCallbacks
  // stable-identity so input children don't re-receive new callback refs on
  // every keystroke.
  const optionsRef = useRef(options);

  useEffect(() => {
    optionsRef.current = options;
  });

  // Fields just written by a handler response — the cascade guard in fireEvent
  // suppresses the echo events their state commit produces.
  const setByHandlerRef = useRef<Map<string, unknown>>(new Map());
  // Last formPatch seen per trigger key (method name, or the positional
  // output@field#index key). Used to suppress repeat calls with the same
  // payload regardless of control type or timing.
  const lastFiredFormPatchRef = useRef<
    Map<string, Record<string, unknown> | undefined>
  >(new Map());
  const debouncedRunnersRef = useRef<Map<string, DebouncedFunc<TriggerRunner>>>(
    new Map()
  );
  const touchedSinceFocusInRef = useRef<Map<string, boolean>>(new Map());

  // Value-aware dedup shared by method and output triggers: if the trigger
  // was last fired with the same formPatch, skip — nothing to recompute.
  // Different payload (or first call) passes through. formPatch=undefined
  // (focus events, header) is never deduped so each fires.
  const shouldSkipDuplicate = useCallback(
    (key: string, formPatch?: Record<string, unknown>): boolean => {
      if (
        formPatch !== undefined &&
        lastFiredFormPatchRef.current.has(key) &&
        isEqual(lastFiredFormPatchRef.current.get(key), formPatch)
      ) {
        /** @debugFeature handlers Logs a trigger skipped by the value-aware dedup — it last fired with the same formPatch. */
        log('duplicate trigger skipped', () => ({ key, formPatch }));
        return true;
      }
      lastFiredFormPatchRef.current.set(key, formPatch);
      return false;
    },
    []
  );

  // Merges a handler result (API response or local output) into the host
  // state: normalize → mark fields as handler-written BEFORE the state update
  // so the cascade guard in fireEvent skips any `change`-triggered handlers on
  // them (value stored too — guard suppresses only genuine echoes) → single
  // state merge for all fields.
  const applyHandlerResult = useCallback(
    (
      fieldUpdates: Record<string, unknown>,
      context: HandlerRequestContext
    ): void => {
      const normalized = normalizeHandlerFieldUpdates(
        fieldUpdates,
        optionsRef.current.getItems()
      );
      const entries = Object.entries(normalized);
      if (entries.length === 0) {
        /** @debugFeature handlers Logs a handler result that normalized to no field updates — the form stays untouched. */
        log('handler result empty', () => ({ fieldUpdates }));
        return;
      }

      entries.forEach(([field, value]) => {
        setByHandlerRef.current.set(field, value);
      });

      /** @debugFeature handlers Logs the normalized field updates a handler merged into the host state. */
      log('handler result applied', () => ({ updates: normalized }));

      optionsRef.current.applyFieldUpdates(normalized, context);
    },
    []
  );

  // Runs one handler method end to end: dedup check → apiMethod lookup →
  // template context build → HTTP request → response merged into the host state.
  const runMethod = useCallback<RunHandlerMethod>(
    async (method, triggerData, formPatch, focusedItem) => {
      // Drop any pending debounced call for this method — we're firing now.
      debouncedRunnersRef.current.get(method)?.cancel();

      if (shouldSkipDuplicate(method, formPatch)) {
        return;
      }

      // Lookup the apiMethod definition by name.
      const apiMethod = optionsRef.current.getApiMethod(method);
      if (!apiMethod) {
        console.warn(
          `Handler method "${method}" is not defined in form.apiMethods`
        );
        return;
      }

      // Base context for JSONata placeholders.
      const baseContext = optionsRef.current.buildContext(
        formPatch,
        focusedItem
      );
      if (!baseContext) {
        console.warn(
          `Handler "${method}" fired without RequestData; skipping.`
        );
        return;
      }

      // Resolve JSONata in triggerData against the base context before merging —
      // header buttons can pass templates like { command: '{{ form.type }}' }.
      const resolvedTriggerData = triggerData
        ? await resolveTemplate(triggerData, baseContext)
        : undefined;

      // triggerData (resolved) is exposed under a dedicated `triggerData` key,
      // so apiMethod templates reference it as `{{ triggerData.command }}`.
      const context = {
        ...baseContext,
        ...(resolvedTriggerData ? { triggerData: resolvedTriggerData } : {}),
      } as HandlerRequestContext;

      /** @debugFeature handlers Logs a handler method run: method name, the firing formPatch, focused item and resolved trigger data. */
      log('handler method run', () => ({
        method,
        formPatch,
        focusedItem,
        triggerData: resolvedTriggerData,
      }));

      // Resolve templates, fire the HTTP request, then merge the returned
      // { field: value } map into the host state. forceHeader is forwarded on retry.
      const runHandlerRequest = async (forceHeader?: string): Promise<void> => {
        const fieldUpdates = await executeHandler(
          apiMethod,
          context,
          optionsRef.current.getResponseActionHandler,
          forceHeader
        );
        applyHandlerResult(fieldUpdates, context);
      };

      // Recursive force retry: passes itself as forceModalAction so chained
      // ASW-FORCE/entry errors re-open the modal and retry with the appended header.
      const retryWithForce = async (forceHeader: string): Promise<void> => {
        try {
          await runHandlerRequest(forceHeader);
        } catch (retryError) {
          await optionsRef.current.handleApiError(
            retryError,
            false,
            retryWithForce
          );
        }
      };

      try {
        await runHandlerRequest();
      } catch (error) {
        await optionsRef.current.handleApiError(error, false, retryWithForce);
      }
    },
    [shouldSkipDuplicate, applyHandlerResult]
  );

  // Runs one output trigger end to end: dedup check → template context build →
  // local resolve (JSONata expression or JSON template) → result merged into
  // the host state. No HTTP call, so no API error handling / force retry —
  // a broken expression just logs and leaves the form untouched.
  const runOutput = useCallback(
    async (
      key: string,
      output: string | JSONObject,
      formPatch?: Record<string, unknown>,
      focusedItem?: string
    ): Promise<void> => {
      // Drop any pending debounced call for this trigger — we're firing now.
      debouncedRunnersRef.current.get(key)?.cancel();

      if (shouldSkipDuplicate(key, formPatch)) {
        return;
      }

      const context = optionsRef.current.buildContext(formPatch, focusedItem);
      if (!context) {
        console.warn(
          `Output handler "${key}" fired without RequestData; skipping.`
        );
        return;
      }

      /** @debugFeature handlers Logs an output trigger run: its positional key and the firing formPatch. */
      log('output trigger run', () => ({ key, formPatch }));

      try {
        const fieldUpdates = await executeOutputHandler(output, context);
        applyHandlerResult(fieldUpdates, context);
      } catch (error) {
        console.error(`Output handler "${key}" failed to resolve:`, error);
      }
    },
    [shouldSkipDuplicate, applyHandlerResult]
  );

  // Returns the debounced wrapper of the given trigger (used for typing in
  // text inputs — the run fires after the typing pauses).
  const getDebouncedRunner = useCallback(
    (key: string, run: TriggerRunner): DebouncedFunc<TriggerRunner> => {
      // Reuse one debounce instance per trigger key so repeated calls reset
      // its timer instead of spawning parallel pending callbacks.
      const existing = debouncedRunnersRef.current.get(key);
      if (existing) return existing;

      // Method triggers are keyed by method (not by field) — multiple fields
      // triggering the same method collapse into a single debounced call.
      // Output triggers are keyed per field+position.
      const runner = debounce<TriggerRunner>(
        (formPatch, focusedItem) => run(formPatch, focusedItem),
        HANDLER_CHANGE_DEBOUNCE_MS
      );
      debouncedRunnersRef.current.set(key, runner);

      return runner;
    },
    []
  );

  // Entry point of a field event: finds the field's triggers for the action
  // and runs their methods (debounced for typing, immediately otherwise).
  const fireEvent = useCallback<FireHandlerEvent>(
    (fieldName, control, action, formPatch) => {
      // Cascade guard — when a handler just wrote this field, suppress the
      // user-intent events (change / touch / focusOutChanged) only when the
      // incoming value matches the handler-written one (genuine echo from the
      // state commit). User-driven change to a different value passes through.
      // Consume the flag either way so it can't mis-trigger later.
      if (
        (action === 'change' ||
          action === 'touch' ||
          action === 'focusOutChanged') &&
        setByHandlerRef.current.has(fieldName)
      ) {
        const handlerValue = setByHandlerRef.current.get(fieldName);
        const newValue = formPatch?.[fieldName];
        setByHandlerRef.current.delete(fieldName);
        if (isEqual(newValue, handlerValue)) {
          /** @debugFeature handlers Logs a field event suppressed by the cascade guard — a genuine echo of a handler-written value. */
          log('handler echo event suppressed', () => ({
            fieldName,
            action,
            value: newValue,
          }));
          return;
        }
      }

      // Look up handlerTriggers configured for this field; bail if none.
      const item = optionsRef.current
        .getItems()
        .find((formItem) => formItem?.name === fieldName);
      const triggers: FormItemHandlerTrigger[] = item?.handlerTriggers ?? [];
      if (triggers.length === 0) return;

      // Collect triggers whose actions include the current action. Methods
      // dedup via Set in case the same method is declared in multiple
      // triggers; output triggers have no name, so each runs under its own
      // positional key (index within the item's handlerTriggers).
      const methods = new Set<string>();
      const outputs: { key: string; output: string | JSONObject }[] = [];
      triggers.forEach((trigger, index) => {
        if (!trigger.actions.includes(action)) return;

        if (trigger.method) {
          methods.add(trigger.method);
        } else if (trigger.output !== undefined) {
          outputs.push({
            key: `output@${fieldName}#${index}`,
            output: trigger.output,
          });
        }
      });
      if (methods.size === 0 && outputs.length === 0) return;

      // Text-like controls debounce `change` (fire after ~1s of no typing);
      // everything else (combobox, date, checkbox) fires `change` immediately
      // because those values come from discrete user gestures, not typing.
      const isTextChange =
        action === 'change' &&
        !!control?.type &&
        HANDLER_TEXT_LIKE_CONTROL_TYPES.has(control.type);

      /** @debugFeature handlers Logs a field event that matched handler triggers: the methods and outputs about to run and whether the run is debounced. */
      log('field event matched handler triggers', () => ({
        fieldName,
        action,
        methods: Array.from(methods),
        outputKeys: outputs.map(({ key }) => key),
        debounced: isTextChange,
      }));

      const fireTrigger = (key: string, run: TriggerRunner): void => {
        if (isTextChange) {
          getDebouncedRunner(key, run)(formPatch, fieldName);
        } else {
          run(formPatch, fieldName);
        }
      };

      methods.forEach((method) => {
        fireTrigger(method, (patch, focused) =>
          runMethod(method, undefined, patch, focused)
        );
      });
      outputs.forEach(({ key, output }) => {
        fireTrigger(key, (patch, focused) =>
          runOutput(key, output, patch, focused)
        );
      });
    },
    [runMethod, runOutput, getDebouncedRunner]
  );

  // Builds the input callbacks (onChange/onTouch/onFocusIn/onFocusOut) for a
  // field — only the ones its triggers actually configure. focusOutChanged has
  // no callback of its own: onFocusOut derives it from the touched flag.
  const getFieldCallbacks = useCallback<
    FieldHandlerEngine['getFieldCallbacks']
  >(
    (name, control, triggers) => {
      if (!triggers || triggers.length === 0) return {};

      // Flatten triggers into a set of all configured actions — decides which
      // callbacks we expose below.
      const actionSet = new Set<HandlerAction>();
      triggers.forEach((t) => {
        t.actions.forEach((a) => actionSet.add(a));
      });

      const callbacks: FieldHandlerCallbacks = {};
      // focusOutChanged + focusIn both depend on the "user changed value since
      // focusIn" flag. We must track it even on fields without a `change`
      // handler — hence onChange/onFocusIn bookkeeping below even when silent.
      const needsTouchedFlag =
        actionSet.has('focusOutChanged') || actionSet.has('focusIn');

      if (actionSet.has('change')) {
        callbacks.onChange = (value) => {
          touchedSinceFocusInRef.current.set(name, true);
          fireEvent(name, control, 'change', { [name]: value });
        };
      } else if (needsTouchedFlag) {
        // No `change` handler configured, but we still need the touched flag
        // for focusOutChanged derivation.
        callbacks.onChange = () => {
          touchedSinceFocusInRef.current.set(name, true);
        };
      }

      if (actionSet.has('touch')) {
        callbacks.onTouch = (value) => {
          fireEvent(
            name,
            control,
            'touch',
            value !== undefined ? { [name]: value } : undefined
          );
        };
      }

      if (actionSet.has('focusIn') || needsTouchedFlag) {
        // Always reset the touched flag on focusIn — onFocusOut reads it to
        // decide whether focusOutChanged fires.
        callbacks.onFocusIn = () => {
          touchedSinceFocusInRef.current.set(name, false);
          if (actionSet.has('focusIn')) {
            fireEvent(name, control, 'focusIn');
          }
        };
      }

      if (actionSet.has('focusOut') || actionSet.has('focusOutChanged')) {
        // focusOutChanged is derived: fires only when the user changed the
        // value since focusIn. Clear the flag afterwards so the next focus
        // cycle starts clean.
        callbacks.onFocusOut = () => {
          if (actionSet.has('focusOut')) {
            fireEvent(name, control, 'focusOut');
          }
          if (
            actionSet.has('focusOutChanged') &&
            touchedSinceFocusInRef.current.get(name)
          ) {
            fireEvent(name, control, 'focusOutChanged');
          }
          touchedSinceFocusInRef.current.set(name, false);
        };
      }

      return callbacks;
    },
    [fireEvent]
  );

  // Forgets the last payload of the method so its next run is never deduped
  // (header buttons must fire on every click).
  const clearMethodDedup = useCallback((method: string): void => {
    lastFiredFormPatchRef.current.delete(method);
  }, []);

  // Unmount cleanup: cancel pending debounced runs so they can't fire later.
  useEffect(() => {
    const runners = debouncedRunnersRef.current;
    return () => {
      runners.forEach((d) => d.cancel());
      runners.clear();
    };
  }, []);

  return useMemo(
    () => ({ runMethod, fireEvent, getFieldCallbacks, clearMethodDedup }),
    [runMethod, fireEvent, getFieldCallbacks, clearMethodDedup]
  );
};
